/**
 * Post-processing cleanup pass.
 *
 * - Remove isolated patches smaller than a threshold
 * - Smooth ragged edges via cellular automata
 * - Enforce ground footing under objects (e.g. dirt under trees)
 * - Graduate object sizes near features ("barnacle" growth)
 */
export class CleanupPass {
  /**
   * @param {Array<Array<number>>} grid — ground layer (mutated in-place)
   * @param {number} width
   * @param {number} height
   * @param {object} options
   * @param {number}  [options.minPatchSize=4]
   * @param {number}  [options.smoothIterations=1]
   * @param {number}  [options.fillTile] — tile used to fill removed patches
   * @param {object}  [options.footingRules] — { objectTileId: groundTileId }
   * @param {Array<Array<number>>} [options.objectGrid] — object layer (for footing / barnacle)
   * @returns {Array<Array<number>>}
   */
  process(grid, width, height, options = {}) {
    const opts = {
      minPatchSize: 4,
      smoothIterations: 1,
      fillTile: null,
      ...options,
    };

    if (opts.minPatchSize > 0) {
      this.removeIsolated(grid, width, height, opts.minPatchSize, opts.fillTile);
    }

    if (opts.smoothIterations > 0) {
      this.smoothEdges(grid, width, height, opts.smoothIterations);
    }

    if (opts.footingRules && opts.objectGrid) {
      this.enforceFooting(grid, opts.objectGrid, width, height, opts.footingRules);
    }

    if (opts.objectGrid && opts.barnacleRules) {
      this.barnacle(grid, opts.objectGrid, width, height, opts.barnacleRules);
    }

    return grid;
  }

  /**
   * Remove contiguous patches of any terrain that are smaller than minSize.
   * Replaces them with the most common neighbour terrain (or fillTile).
   */
  removeIsolated(grid, width, height, minSize, fillTile) {
    const visited = [];
    for (let y = 0; y < height; y++) visited.push(new Uint8Array(width));

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (visited[y][x]) continue;
        const terrainId = grid[y][x];

        // BFS to find patch
        const patch = [];
        const queue = [[x, y]];
        visited[y][x] = 1;

        while (queue.length > 0) {
          const [cx, cy] = queue.pop();
          patch.push([cx, cy]);

          const neighbors = [
            [cx - 1, cy], [cx + 1, cy], [cx, cy - 1], [cx, cy + 1],
          ];
          for (const [nx, ny] of neighbors) {
            if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
            if (visited[ny][nx]) continue;
            if (grid[ny][nx] !== terrainId) continue;
            visited[ny][nx] = 1;
            queue.push([nx, ny]);
          }
        }

        if (patch.length >= minSize) continue;

        // Find most common adjacent terrain to replace with
        const adj = new Map();
        for (const [px, py] of patch) {
          for (const [nx, ny] of [[px - 1, py], [px + 1, py], [px, py - 1], [px, py + 1]]) {
            if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
            const nTerrain = grid[ny][nx];
            if (nTerrain !== terrainId) {
              adj.set(nTerrain, (adj.get(nTerrain) || 0) + 1);
            }
          }
        }

        let replacement = fillTile;
        if (replacement === null || replacement === undefined) {
          let maxCount = 0;
          for (const [t, c] of adj) {
            if (c > maxCount) { maxCount = c; replacement = t; }
          }
          if (replacement === null || replacement === undefined) replacement = terrainId;
        }

        for (const [px, py] of patch) {
          grid[py][px] = replacement;
        }
      }
    }
  }

  /**
   * Cellular automata smoothing: a tile adopts the majority of its
   * 8 neighbours if at least 5/8 agree.
   */
  smoothEdges(grid, width, height, iterations) {
    for (let iter = 0; iter < iterations; iter++) {
      const snapshot = grid.map(row => [...row]);

      for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
          const counts = new Map();
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              if (dx === 0 && dy === 0) continue;
              const t = snapshot[y + dy][x + dx];
              counts.set(t, (counts.get(t) || 0) + 1);
            }
          }

          let majority = snapshot[y][x];
          let majorityCount = 0;
          for (const [t, c] of counts) {
            if (c > majorityCount) { majorityCount = c; majority = t; }
          }

          if (majorityCount >= 5) {
            grid[y][x] = majority;
          }
        }
      }
    }
  }

  /**
   * Replace the ground tile under objects to provide visual footing.
   * E.g. dirt under trees, stone under statues.
   *
   * @param {object} footingRules — { objectTileId: groundTileId }
   */
  enforceFooting(grid, objectGrid, width, height, footingRules) {
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const obj = objectGrid[y][x];
        if (obj === 0) continue;
        const footing = footingRules[obj];
        if (footing !== undefined) {
          grid[y][x] = footing;
        }
      }
    }
  }

  /**
   * Graduate object density near features — smaller decorations cluster
   * around larger objects, giving organic edge growth.
   *
   * @param {object} barnacleRules — { sourceTileId: { tile: id, radius: n, chance: 0-1 } }
   */
  barnacle(grid, objectGrid, width, height, barnacleRules) {
    // Snapshot to avoid cascading
    const snapshot = objectGrid.map(row => [...row]);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const obj = snapshot[y][x];
        if (obj === 0) continue;
        const rule = barnacleRules[obj];
        if (!rule) continue;

        const { tile, radius, chance } = rule;
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = x + dx, ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
            if (objectGrid[ny][nx] !== 0) continue;
            if (Math.random() < chance) {
              objectGrid[ny][nx] = tile;
            }
          }
        }
      }
    }
  }
}
